import {
  COUNCIL_DATASET_CSV_PATH,
  COUNCIL_DATASET_JSON_PATH,
  COUNCIL_DATASET_METADATA,
  DFT_RATINGS_SOURCE,
  OPEN_GOVERNMENT_LICENCE_V3,
} from '@fixtyer/content';
import { Link } from 'react-router';
import {
  BreadcrumbSchema,
  DatasetSchema,
  SEO,
} from '@/components/seo/seo';

const fields = [
  ['authority_id', 'Stable ID derived from the authority name in the DfT table.'],
  ['slug', 'Stable Fixtyer slug; a page is not implied when report_url is null.'],
  ['canonical_name', 'Authority name transcribed from the DfT rated-authority table.'],
  ['display_name', 'Reader-facing Fixtyer name for the same authority.'],
  ['nation / region', 'England and the checked-in regional grouping used by Fixtyer.'],
  ['ratings', 'DfT overall, condition, spend and best-practice bands. A null component is not inferred.'],
  ['report_url', 'Existing Fixtyer council report URL, or null where no page exists.'],
  ['sources', 'Source document URL, title, publisher, date and licence where recorded.'],
  ['observedFacts / observed_facts_json', 'Authority-specific observations with source URL, report period and cautious interpretation where verified.'],
  ['provenance', 'Observed source fact and any normalisation or interpretation note for each field group.'],
  ['entity', 'Previously verified official-site, Wikidata and Wikipedia links, otherwise null.'],
  ['published / reviewed', 'ISO dates for this Fixtyer dataset entry.'],
  ['correction', 'Current correction status and the correction-process URL.'],
] as const;

export function CouncilRoadMaintenanceDatasetPage() {
  return (
    <div className="min-h-screen bg-surface-50">
      <SEO
        title="England Council Road Maintenance Ratings Dataset 2025-2026 v1 | Fixtyer"
        description="Download the citable Fixtyer publishing dataset for all 154 DfT-rated English highway authorities, with field-level provenance in JSON and CSV."
        canonical="/data/council-road-maintenance-ratings"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Council ratings data', url: '/data/council-road-maintenance-ratings' },
        ]}
      />
      <DatasetSchema
        name={COUNCIL_DATASET_METADATA.title}
        description="DfT 2025-2026 road-maintenance rating bands for all 154 rated English highway authorities, published by Fixtyer with field-level provenance and cautious interpretation."
        url="/data/council-road-maintenance-ratings"
        datePublished={COUNCIL_DATASET_METADATA.publishedDate}
        dateModified={COUNCIL_DATASET_METADATA.modifiedDate}
        license={OPEN_GOVERNMENT_LICENCE_V3}
        temporalCoverage={COUNCIL_DATASET_METADATA.temporalCoverage}
        spatialCoverage={COUNCIL_DATASET_METADATA.spatialCoverage}
        keywords={['DfT', 'road maintenance', 'English highway authorities', '2025-2026']}
        variableMeasured={['Overall rating', 'Condition rating', 'Spend rating', 'Best-practice rating']}
        distribution={[
          {
            name: 'Council road maintenance ratings 2025-2026 v1 CSV',
            contentUrl: COUNCIL_DATASET_CSV_PATH,
            encodingFormat: 'text/csv',
          },
          {
            name: 'Council road maintenance ratings 2025-2026 v1 JSON',
            contentUrl: COUNCIL_DATASET_JSON_PATH,
            encodingFormat: 'application/json',
          },
        ]}
      />

      <header className="bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <p className="text-amber-300 font-semibold mb-3">Open publishing dataset</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold">
            England council road maintenance ratings 2025-2026 v1
          </h1>
          <p className="mt-5 text-lg text-brand-100 max-w-3xl">
            A citable export of the four published DfT rating bands for all 154 rated
            English highway authorities, with source documents, field-level provenance
            and claim-safe context.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a
              href={COUNCIL_DATASET_CSV_PATH}
              className="rounded-lg bg-accent-500 px-5 py-3 font-semibold text-white hover:bg-accent-600"
            >
              Download CSV (2025-2026 v1)
            </a>
            <a
              href={COUNCIL_DATASET_JSON_PATH}
              className="rounded-lg bg-white px-5 py-3 font-semibold text-brand-900 hover:bg-brand-50"
            >
              Download JSON (2025-2026 v1)
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-12">
        <section className="grid sm:grid-cols-3 gap-4" aria-label="Dataset summary">
          <div className="rounded-xl border border-surface-200 bg-white p-5">
            <strong className="text-3xl text-brand-900">154</strong>
            <p className="text-surface-600">rated English highway authorities</p>
          </div>
          <div className="rounded-xl border border-surface-200 bg-white p-5">
            <strong className="text-lg text-brand-900">Published 19 July 2026</strong>
            <p className="text-surface-600">Modified 19 July 2026</p>
          </div>
          <div className="rounded-xl border border-surface-200 bg-white p-5">
            <strong className="text-lg text-brand-900">England only</strong>
            <p className="text-surface-600">Wales uses a separate system and is not DfT-rated here.</p>
          </div>
        </section>

        <section>
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">Methodology</h2>
          <div className="space-y-3 text-surface-700">
            <p>
              The authority names and overall, condition, spend and best-practice bands are
              transcribed from the checked-in copy of the Department for Transport publication.
              Fixtyer does not calculate or infer a missing rating.
            </p>
            <p>
              IDs and slugs are deterministic normalisations. Existing council report routes and
              verified entity links are matched independently by slug. Current measured coverage
              is 153 of 154 for both report pages and verified entities; Transport for London
              remains null rather than receiving guessed links.
            </p>
            <p>
              Ratings describe network-level bands. They do not determine liability, knowledge
              of a particular defect, or whether an inspection and repair system was reasonable
              at an incident location.
            </p>
          </div>
        </section>

        <section>
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">Data dictionary</h2>
          <dl className="grid gap-3">
            {fields.map(([name, description]) => (
              <div key={name} className="rounded-lg border border-surface-200 bg-white p-4">
                <dt className="font-mono font-semibold text-brand-900">{name}</dt>
                <dd className="mt-1 text-surface-600">{description}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section>
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Field-level provenance
          </h2>
          <p className="text-surface-700 mb-4">
            Each JSON authority record and CSV row carries provenance for identity, ratings,
            report URL, entities and interpretation. Every note separates the observed source
            fact from Fixtyer's normalisation or cautious calculation note.
          </p>
          <p className="text-surface-700 mb-4">
            Authority-specific observations are included only where an existing transparency
            source, report period and cautious interpretation are recorded.
          </p>
          <p className="text-surface-700">
            Source publisher:{' '}
            <a className="text-brand-700 underline" href={DFT_RATINGS_SOURCE.url}>
              Department for Transport — {DFT_RATINGS_SOURCE.title}
            </a>.
          </p>
        </section>

        <section>
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Licence and attribution
          </h2>
          <p className="text-surface-700">
            Contains public sector information licensed under the{' '}
            <a className="text-brand-700 underline" href={OPEN_GOVERNMENT_LICENCE_V3}>
              Open Government Licence v3.0
            </a>. Source: Department for Transport, Local road maintenance ratings 2025 to 2026.
            Fixtyer is the creator and publisher of this provenance-enriched distribution.
          </p>
        </section>

        <section id="corrections">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">Corrections</h2>
          <p className="text-surface-700">
            No correction is currently recorded. To report a source, transcription or entity
            issue, use the <Link className="text-brand-700 underline" to="/contact">contact form</Link>{' '}
            and identify the authority, field and supporting source. Accepted corrections update
            the modified date and changelog without silently rewriting a published version.
          </p>
        </section>

        <section>
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">Changelog</h2>
          <div className="rounded-lg border border-surface-200 bg-white p-5">
            <p className="font-semibold text-brand-900">2025-2026 v1 — 19 July 2026</p>
            <p className="mt-1 text-surface-600">
              Initial typed publication: 154 DfT-rated English authorities, deterministic JSON
              and CSV distributions, field-level provenance, measured 153-page/entity coverage,
              and cautious claim context.
            </p>
          </div>
        </section>

        <nav className="flex flex-wrap gap-4 border-t border-surface-200 pt-8" aria-label="Related rating resources">
          <Link className="text-brand-700 underline" to="/authority-ratings">Filter authority ratings</Link>
          <Link className="text-brand-700 underline" to="/road-maintenance-report">Read the national report</Link>
          <Link className="text-brand-700 underline" to="/councils">Browse council reports</Link>
        </nav>
      </main>
    </div>
  );
}
